import { useEffect, useState } from 'react';
import { Bell, Languages, Moon, RotateCcw, Settings, Sun, X } from 'lucide-react';
import { DEFAULT_SETTINGS, useSettings } from '../settings';
import PremiumSelect from './PremiumSelect';
import TutorialCenter from '../onboarding/TutorialCenter';

function ToggleSwitch({ checked, onChange, label, description }) {
  return (
    <label className="settings-toggle-row">
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}

function SettingsPanel({ open, onClose }) {
  const { settings, updateSetting, resetSettings, t } = useSettings();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    setSaved(true);
    const id = window.setTimeout(() => setSaved(false), 1400);
    return () => window.clearTimeout(id);
  }, [settings.language, settings.theme, settings.notifications, open]);

  if (!open) return null;

  const setTheme = (theme) => updateSetting('theme', theme);

  return (
    <div className="settings-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="settings-modal panel glow-card" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <button type="button" className="settings-close" onClick={onClose} aria-label={t('settings.close')}>
          <X size={18} />
        </button>

        <div className="settings-head">
          <div className="settings-head-icon"><Settings size={20} /></div>
          <div>
            <p className="eyebrow">{t('nav.settings')}</p>
            <h2 id="settings-title">{t('settings.title')}</h2>
            <p>{t('settings.subtitle')}</p>
          </div>
        </div>

        <div className="settings-grid">
          <div className="settings-card" data-tour="settings-language">
            <div className="settings-card-title">
              <Languages size={18} />
              <div>
                <h3>{t('settings.language')}</h3>
                <p>{t('settings.languageHelp')}</p>
              </div>
            </div>
            <PremiumSelect
              name="language"
              value={settings.language}
              onChange={(event) => updateSetting('language', event.target.value)}
              ariaLabel={t('settings.language')}
              options={[
                { value: 'en', label: t('settings.english') },
                { value: 'de', label: t('settings.german') },
              ]}
            />
          </div>

          <div className="settings-card" data-tour="settings-theme">
            <div className="settings-card-title">
              {settings.theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
              <div>
                <h3>{t('settings.theme')}</h3>
                <p>{t('settings.themeHelp')}</p>
              </div>
            </div>
            <div className="theme-switch" role="group" aria-label={t('settings.theme')}>
              <button type="button" className={settings.theme === 'light' ? 'active' : ''} onClick={() => setTheme('light')}>
                <Sun size={15} /> {t('settings.light')}
              </button>
              <button type="button" className={settings.theme === 'dark' ? 'active' : ''} onClick={() => setTheme('dark')}>
                <Moon size={15} /> {t('settings.dark')}
              </button>
            </div>
          </div>

          <div className="settings-card settings-card-wide" data-tour="settings-notifications">
            <ToggleSwitch
              checked={settings.notifications}
              onChange={(value) => updateSetting('notifications', value)}
              label={t('settings.notifications')}
              description={t('settings.notificationsHelp')}
            />
          </div>

          <TutorialCenter onBeforeStart={onClose} />
        </div>

        <div className="settings-actions">
          <span className={`settings-saved ${saved ? 'visible' : ''}`}>{t('settings.saved')}</span>
          <button type="button" className="secondary-button" onClick={() => resetSettings(DEFAULT_SETTINGS)}>
            <RotateCcw size={15} /> {t('settings.reset')}
          </button>
          <button type="button" className="primary-button" onClick={onClose}>
            <Bell size={15} /> {t('settings.close')}
          </button>
        </div>
      </section>
    </div>
  );
}

export default SettingsPanel;
